// eslint-disable-next-line import/no-cycle
import { Property } from '@/types';
// eslint-disable-next-line import/no-cycle
import {
  TemplateEditorState,
  updateSectionsInState,
  setTemplateConfigurationKeyInState,
} from '@/composables/template-editor/TemplateEditor';
// eslint-disable-next-line import/no-cycle
import { getShopDefaultLang, UserState } from '@/composables/User';
// eslint-disable-next-line import/no-cycle
import { TRANSLATION_MARKUP } from '@/components/template-builder/utils/constants';
// eslint-disable-next-line import/no-cycle
import { getTemplateIframeDocument } from '@/components/template-builder/utils/helpers';
// eslint-disable-next-line import/no-cycle
import { setTranslationInState } from '@/components/template-builder/utils/translate';
// eslint-disable-next-line import/no-cycle
import { nestCall, NestApiType } from '@/composables/nestApi';
// eslint-disable-next-line import/no-cycle
import { store } from '@/store';

interface RcsSmartListConfig {
  [key: string]: any;
}

interface CarouselFillers {
  discover: string;
  description: string;
  price: string;
}

const carouselTranslations: Record<string, CarouselFillers> = {
  fr: { discover: '✨ Découvrir : ', description: '\n Description : ', price: '\n Prix : ' },
  en: { discover: '✨ Discover : ', description: '\n Description : ', price: '\n Price : ' },
  es: { discover: '✨ Descubrir : ', description: '\n Descripción : ', price: '\n Precio : ' },
  pt: { discover: '✨ Descobrir : ', description: '\n Descrição : ', price: '\n Preço : ' },
  de: { discover: '✨ Entdecken : ', description: '\n Beschreibung : ', price: '\n Preis : ' },
  it: { discover: '✨ Scopri : ', description: '\n Descrizione : ', price: '\n Prezzo : ' },
  nl: { discover: '✨ Ontdek : ', description: '\n Beschrijving : ', price: '\n Prijs : ' },
  pl: { discover: '✨ Odkryj : ', description: '\n Opis : ', price: '\n Cena : ' },
  ru: { discover: '✨ Открыть : ', description: '\n Описание : ', price: '\n Цена : ' },
  ar: { discover: '✨ اكتشف : ', description: '\n الوصف : ', price: '\n السعر : ' },
  tr: { discover: '✨ Keşfet : ', description: '\n Açıklama : ', price: '\n Fiyat : ' },
  ja: { discover: '✨ 発見 : ', description: '\n 説明 : ', price: '\n 価格 : ' },
  ko: { discover: '✨ 발견 : ', description: '\n 설명 : ', price: '\n 가격 : ' },
  zh: { discover: '✨ 发现 : ', description: '\n 描述 : ', price: '\n 价格 : ' },
  th: { discover: '✨ ค้นพบ : ', description: '\n คำอธิบาย : ', price: '\n ราคา : ' },
  vi: { discover: '✨ Khám phá : ', description: '\n Mô tả : ', price: '\n Giá : ' },
  id: { discover: '✨ Temukan : ', description: '\n Deskripsi : ', price: '\n Harga : ' },
  hi: { discover: '✨ खोजें : ', description: '\n विवरण : ', price: '\n कीमत : ' },
  bn: { discover: '✨ আবিষ্কার : ', description: '\n বিবরণ : ', price: '\n মূল্য : ' },
  el: { discover: '✨ Ανακάλυψε : ', description: '\n Περιγραφή : ', price: '\n Τιμή : ' },
  cs: { discover: '✨ Objevte : ', description: '\n Popis : ', price: '\n Cena : ' },
  ro: { discover: '✨ Descoperă : ', description: '\n Descriere : ', price: '\n Preț : ' },
  hu: { discover: '✨ Felfedezés : ', description: '\n Leírás : ', price: '\n Ár : ' },
  sv: { discover: '✨ Upptäck : ', description: '\n Beskrivning : ', price: '\n Pris : ' },
  da: { discover: '✨ Opdag : ', description: '\n Beskrivelse : ', price: '\n Pris : ' },
  fi: { discover: '✨ Löydä : ', description: '\n Kuvaus : ', price: '\n Hinta : ' },
  no: { discover: '✨ Oppdag : ', description: '\n Beskrivelse : ', price: '\n Pris : ' },
  uk: { discover: '✨ Відкрийте : ', description: '\n Опис : ', price: '\n Ціна : ' },
  he: { discover: '✨ גלה : ', description: '\n תיאור : ', price: '\n מחיר : ' },
  ms: { discover: '✨ Temui : ', description: '\n Penerangan : ', price: '\n Harga : ' },
  tl: { discover: '✨ Tuklasin : ', description: '\n Paglalarawan : ', price: '\n Presyo : ' },
  sk: { discover: '✨ Objavte : ', description: '\n Popis : ', price: '\n Cena : ' },
  bg: { discover: '✨ Открийте : ', description: '\n Описание : ', price: '\n Цена : ' },
  hr: { discover: '✨ Otkrijte : ', description: '\n Opis : ', price: '\n Cijena : ' },
  sr: { discover: '✨ Откријте : ', description: '\n Опис : ', price: '\n Цена : ' },
  sl: { discover: '✨ Odkrijte : ', description: '\n Opis : ', price: '\n Cena : ' },
  et: { discover: '✨ Avasta : ', description: '\n Kirjeldus : ', price: '\n Hind : ' },
  lv: { discover: '✨ Atklāj : ', description: '\n Apraksts : ', price: '\n Cena : ' },
  lt: { discover: '✨ Atrask : ', description: '\n Aprašymas : ', price: '\n Kaina : ' },
};

/**
 * Get carousel fillers for a given language, fallback to French
 */
const getCarouselFillers = (lang: string): CarouselFillers => carouselTranslations[lang] || carouselTranslations.fr;

/**
 * Get smartlist config from richtexting_data[defaultLang].smartlist
 */
const getCurrentConfig = (): RcsSmartListConfig => {
  const template = TemplateEditorState.template;
  const configuration = template?.configuration as Record<string, any> | undefined;
  const defaultLang = getShopDefaultLang();
  const rcsData = configuration?.richtexting_data as Record<string, any> | undefined;
  return { ...(rcsData?.[defaultLang]?.smartlist ?? {}) } as RcsSmartListConfig;
};

/**
 * Save smartlist config to richtexting_data[lang].smartlist for all languages
 */
const saveConfig = (config: RcsSmartListConfig) => {
  const template = TemplateEditorState.template;
  const configuration = template?.configuration as Record<string, any> | undefined;
  const existingRcsData = (configuration?.richtexting_data ?? {}) as Record<string, any>;
  
  // Deep clone richtexting_data to avoid mutating the original
  const rcsData: Record<string, any> = {};
  
  // Update smartlist for all languages
  const languages = UserState.activeShop?.langs.map((lang) => lang.id) ?? [];
  languages.forEach((lang) => {
    // Deep clone each language's data
    rcsData[lang] = {
      ...(existingRcsData[lang] ?? {}),
      smartlist: { ...config },
    };
  });
  
  setTemplateConfigurationKeyInState('richtexting_data', rcsData);
};

/**
 * Update the button text in the hidden source element (for translation) and visible buttons
 */
const updateButtonTextHtml = (selector: string, value: string) => {
  const template = getTemplateIframeDocument();
  if (!template) return;

  const container = template.querySelector(selector) as HTMLElement | null;
  if (!container) return;

  // Update the hidden source element (which has translation markup)
  const source = container.querySelector('.rcs-smartlist-buttontext-source') as HTMLElement | null;
  if (source) {
    const translationNode = source.querySelector(TRANSLATION_MARKUP) as HTMLElement | null;
    if (translationNode) {
      translationNode.innerHTML = value;
      setTranslationInState(source, 'markup');
    } else {
      source.textContent = value;
    }
  }

  // Update all visible button text elements in the carousel
  const buttons = template.querySelectorAll('.rcs-smartlist-buttontext');
  buttons.forEach((button) => {
    button.textContent = value;
  });
};

/**
 * Toggle carousel visibility based on enabled state and refresh products if enabled
 */
const updateCarouselVisibility = async (enabled: boolean) => {
  const template = getTemplateIframeDocument();
  if (!template) return;

  const carousel = template.querySelector('.rcs-smartlist-carousel') as HTMLElement | null;
  if (!carousel) return;

  if (enabled) {
    carousel.classList.add('smartlist-enabled');
    // Refresh products when enabling smartlist
    await refreshRcsSmartlist();
  } else {
    carousel.classList.remove('smartlist-enabled');
  }
};

/**
 * Get CTAs from the body message to display on carousel cards
 */
const getCtasFromBodyMessage = (): Array<{ text: string; translationId: string }> => {
  const template = getTemplateIframeDocument();
  if (!template) return [];

  const ctaListContainer = template.querySelector('.rcs-message__cta__list');
  if (!ctaListContainer) return [];

  const ctaElements = ctaListContainer.querySelectorAll('.rcs-message__cta__item');
  
  // Only take the first CTA (carousel allows max 2 buttons per card, and "Voir le produit" takes 1)
  const ctas: Array<{ text: string; translationId: string }> = [];
  
  if (ctaElements.length > 0) {
    const firstCta = ctaElements[0];
    const translationNode = firstCta.querySelector(TRANSLATION_MARKUP);
    const translationId = translationNode?.getAttribute('id')?.replace('LANG_', '').split('.')[0] || '';
    const text = translationNode?.innerHTML || firstCta.textContent || '';
    ctas.push({ text, translationId });
  }

  return ctas;
};

/**
 * Refresh RCS smartlist products - only if smartlist is enabled
 * @param activeLang - Optional language to use for fillers (defaults to shop default language)
 */
export const refreshRcsSmartlist = async (activeLang?: string) => {
  const template = getTemplateIframeDocument();
  if (!template) return;

  const carousel = template.querySelector('.rcs-smartlist-carousel') as HTMLElement | null;
  if (!carousel) return;

  // Only refresh if smartlist is enabled
  if (!carousel.classList.contains('smartlist-enabled')) return;

  const cardsContainer = carousel.querySelector('.rcs-smartlist-cards') as HTMLElement | null;
  if (!cardsContainer) return;

  // Get config from richtexting_data
  const config = getCurrentConfig();

  // Build parameters for API call
  const parameters = {
    collection: 'products',
    item: 'product',
    grid: '1',
    nb: config.nb || '3',
    engine: config.engine || 'crossselling',
    ids: config.ids || '',
    excluded_ids: config.excluded_ids || '',
    excluded_categories: config.excluded_categories || '',
    excluded_manufacturers: config.excluded_manufacturers || '',
    nb_weeks: config.nb_weeks || '4',
  };

  // Call API to get products - use shop default lang for API, but activeLang for fillers
  const shopLang = getShopDefaultLang();
  const fillersLang = activeLang || shopLang;
  const idShop = UserState.activeShop?.id;
  if (!idShop) return;

  const result = await nestCall('get', NestApiType.DEFAULT, `/template/smart-product-list/${idShop}/${shopLang}/${JSON.stringify(parameters)}`, {}, '', {}, false);
  const rawProducts = (result && result.success && result?.data) ? JSON.parse(result.data) : [];

  if (!rawProducts.length) return;

  // Ensure we have exactly `nb` products by looping if necessary
  const targetCount = parseInt(config.nb || '3', 10);
  const products: any[] = [];
  for (let i = 0; i < targetCount; i++) {
    products.push(rawProducts[i % rawProducts.length]);
  }

  // Get displayed elements from config
  const displayedElements: string[] = config.displayedElements || [
    'spm_product_list_element_title',
    'spm_product_list_element_image',
    'spm_product_list_element_price',
    'spm_product_list_element_description',
  ];

  const showImage = displayedElements.includes('spm_product_list_element_image');
  const showTitle = displayedElements.includes('spm_product_list_element_title');
  const showDescription = displayedElements.includes('spm_product_list_element_description');
  const showPrice = displayedElements.includes('spm_product_list_element_price');

  // Get button text from config or use default
  const buttonText = config.buttonText || 'Voir le produit';

  // Get CTAs from body message to display on cards
  const ctas = getCtasFromBodyMessage();
  const ctaHtml = ctas.map((cta) => `
    <span class="rcs-smartlist-cta" data-translation-id="${cta.translationId}">${cta.text}</span>
  `).join('');

  // Get fillers for the current language
  const fillers = getCarouselFillers(fillersLang);

  // Generate HTML for each product (parent container has spm_no_translate to exclude from translation)
  const productsHtml = products.map((product: any) => `
    <div class="rcs-smartlist-card">
      ${showImage ? `
        <div class="rcs-smartlist-card__image spm_product_list_element_image">
          <img src="${product.image_url || ''}" alt="${product.name || ''}" />
        </div>
      ` : ''}
      <div class="rcs-smartlist-card__content">
        ${showTitle ? `
          <div class="rcs-smartlist-card__title spm_product_list_element_title"><span class="rcs-smartlist-filler">${fillers.discover}</span>${product.name || ''}</div>
        ` : ''}
        ${showDescription ? `
          <div class="rcs-smartlist-card__description spm_product_list_element_description"><span class="rcs-smartlist-filler">${fillers.description}</span>${product.description_short || ''}</div>
        ` : ''}
        ${showPrice ? `
          <div class="rcs-smartlist-card__price spm_product_list_element_price"><span class="rcs-smartlist-filler">${fillers.price}</span>${product.price || ''}</div>
        ` : ''}
      </div>
      <div class="rcs-smartlist-card__button">
        <span class="rcs-smartlist-buttontext">${buttonText}</span>
        ${ctaHtml}
      </div>
    </div>
  `).join('');

  cardsContainer.innerHTML = productsHtml;
};

export const setRcsSmartListProperty = (selector: string, property: Property, widgetId: string) => {
  if (!property.name) return;

  // For buttonText, only update HTML - value will be extracted in fillRichTextingDataTranslation on save
  if (property.name === 'buttonText' && typeof property.value === 'string') {
    updateButtonTextHtml(selector, property.value);
    updateSectionsInState(false);
    return;
  }

  // For enabled, also toggle carousel visibility and refresh config panel
  if (property.name === 'enabled') {
    updateCarouselVisibility(property.value === 'true');
    // Refresh configuration panel to update disabled state of other fields
    store.commit('liveEditor/refreshConfigurationPanelKey');
  }

  // For other properties, save to richtexting_data.smartlist
  const current = getCurrentConfig();
  const next: RcsSmartListConfig = {
    ...current,
    [property.name]: property.value,
  };

  saveConfig(next);
  updateSectionsInState(false);
};

export const setRcsSmartListDisplayedElements = (selector: string, property: Property, widgetId: string) => {
  const current = getCurrentConfig();
  const next: RcsSmartListConfig = {
    ...current,
    displayedElements: Array.isArray(property.value) ? property.value : [],
  };

  saveConfig(next);
  updateSectionsInState(false);
};

export const setRcsSmartListHtmlCode = (selector: string, property: Property, widgetId: string) => {
  const current = getCurrentConfig();
  const next: RcsSmartListConfig = {
    ...current,
    htmlCode: property.value ?? '',
  };

  saveConfig(next);
  updateSectionsInState(false);
};
